import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useNavigate } from 'react-router-dom';
import { Container, Navbar, Nav, Spinner, NavDropdown } from 'react-bootstrap';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/login';
import ProtectedRoute from './components/ProtectedRoute';
import { InsertBasicData } from './components/insertDataComponents';
import { ListingComponent } from './components/listingsComponents';
import UpdateListingPage from './components/updateListingComponents/UpdateListingPage';
import SalesPage from './components/salesPage/SalesPage';
import Settings from './components/settings';
import { SettingsIcon } from './components/common';
import './theme.css';
import './App.css';

const queryClient = new QueryClient();

// Navigation component
const Navigation = () => {
  const { user, logout, isAuthenticated, isAdmin } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <Navbar variant="dark" expand="lg" className="navbar-app">
      <Container>
        <Navbar.Brand as={Link} to="/">Product Shipping Platform</Navbar.Brand>
        <Navbar.Toggle aria-controls="basic-navbar-nav" />
        <Navbar.Collapse id="basic-navbar-nav">
          <Nav className="me-auto">
            {isAuthenticated && (
              <>
                <Nav.Link as={Link} to="/insert-basic-data">Insert Basic Data</Nav.Link>
                <Nav.Link as={Link} to="/list-items">List items</Nav.Link>
                <Nav.Link as={Link} to="/update-listing">Update listing</Nav.Link>
                <Nav.Link as={Link} to="/sales">Sales</Nav.Link>
              </>
            )}
          </Nav>
          <Nav className="align-items-center">
            {isAuthenticated ? (
              <>
                <NavDropdown title={user?.first_name || 'User'} id="user-dropdown">
                  <NavDropdown.Item onClick={handleLogout}>Logout</NavDropdown.Item>
                </NavDropdown>
                {isAdmin && (
                <Nav.Link as={Link} to="/settings" className="d-flex align-items-center" title="Settings" aria-label="Settings">
                  <SettingsIcon />
                </Nav.Link>
              )}
              </>
            ) : null}
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
};

// Component to handle root route redirect
const RootRedirect = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  return isAuthenticated ? <Navigate to="/insert-basic-data" replace /> : <Navigate to="/login" replace />;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
        <div className="App">
          <Navigation />
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            
            <Route path="/login" element={<Login />} />
            <Route 
              path="/insert-basic-data" 
              element={
                <ProtectedRoute>
                  <InsertBasicData />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/list-items" 
              element={
                <ProtectedRoute>
                  <ListingComponent />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/update-listing" 
              element={
                <ProtectedRoute>
                  <UpdateListingPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/sales" 
              element={
                <ProtectedRoute>
                  <SalesPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/settings" 
              element={
                <ProtectedRoute adminOnly>
                  <Settings />
                </ProtectedRoute>
              } 
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
